import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  MagnifyingGlassIcon,
  PaperClipIcon,
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  EnvelopeOpenIcon,
} from "@heroicons/react/20/solid";
import CorrespondenceTimeline from "./CorrespondenceTimeline";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

/* ---------- helpers ---------- */

const getId = (item) => item?._id?.$oid || item?._id || item?.id;
const getDate = (item) => item?.receivedAt || item?.sentAt;

const attachmentUrl = (correspondenceId, attachment) =>
  `${API_BASE}/correspondence/${correspondenceId}/attachments/${getId(
    attachment,
  )}`;

const getKind = (att) => {
  const type = (att?.contentType || "").toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf") return "pdf";
  return "other";
};

const formatSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const kindStyles = {
  image: { Icon: PhotoIcon, color: "text-purple-300", bg: "bg-purple-500/15" },
  pdf: { Icon: DocumentTextIcon, color: "text-red-300", bg: "bg-red-500/15" },
  other: { Icon: DocumentIcon, color: "text-gray-300", bg: "bg-gray-500/20" },
};

const kindLabel = (att) => {
  const kind = getKind(att);
  if (kind === "pdf") return "PDF";
  if (kind === "image")
    return (att.contentType.split("/")[1] || "IMAGE").toUpperCase();
  return "FILE";
};

// Fetch an attachment with the auth token and return a blob
const fetchAttachmentBlob = async (correspondenceId, attachment) => {
  const token = localStorage.getItem("token");
  const res = await axios.get(attachmentUrl(correspondenceId, attachment), {
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });
  return res.data;
};

const saveBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename || "attachment";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const downloadAttachment = async (correspondenceId, attachment) => {
  try {
    const blob = await fetchAttachmentBlob(correspondenceId, attachment);
    saveBlob(blob, attachment.filename);
  } catch (err) {
    console.error("Download failed:", err);
    alert("Could not download this file.");
  }
};

// Open the window synchronously (avoids popup blockers), then point it at the blob
const openAttachmentInNewTab = async (correspondenceId, attachment) => {
  const win = window.open("", "_blank");
  try {
    const blob = await fetchAttachmentBlob(correspondenceId, attachment);
    const typed = new Blob([blob], {
      type: attachment.contentType || blob.type,
    });
    const objectUrl = URL.createObjectURL(typed);
    if (win) win.location.href = objectUrl;
    else window.location.href = objectUrl;
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (err) {
    console.error("Open failed:", err);
    if (win) win.close();
    alert("Could not open this file.");
  }
};

/* ---------- preview modal ---------- */

function AttachmentPreviewModal({ preview, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  const attachment = preview?.attachment;
  const correspondenceId = preview?.correspondenceId;

  useEffect(() => {
    if (!preview) return;
    let cancelled = false;
    let objectUrl = null;
    setStatus("loading");
    setBlobUrl(null);

    fetchAttachmentBlob(correspondenceId, attachment)
      .then((blob) => {
        if (cancelled) return;
        const typed = new Blob([blob], {
          type: attachment.contentType || blob.type,
        });
        objectUrl = URL.createObjectURL(typed);
        setBlobUrl(objectUrl);
        setStatus("ready");
      })
      .catch((err) => {
        console.error("Preview failed:", err);
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [preview, correspondenceId, attachment]);

  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview, onClose]);

  if (!preview) return null;
  const kind = getKind(attachment);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-gray-600 bg-gray-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-gray-600 px-5 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">
              {attachment.filename}
            </p>
            <p className="text-xs text-gray-400">
              {kindLabel(attachment)}
              {attachment.size != null && ` • ${formatSize(attachment.size)}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadAttachment(correspondenceId, attachment)}
              className="inline-flex items-center gap-1.5 rounded-md bg-gray-600 px-3 py-1.5 text-sm text-gray-100 hover:bg-gray-500"
            >
              <ArrowDownTrayIcon className="size-4" /> Download
            </button>
            <button
              onClick={() => blobUrl && window.open(blobUrl, "_blank")}
              disabled={!blobUrl}
              className="inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-400 disabled:opacity-50"
            >
              <ArrowTopRightOnSquareIcon className="size-4" /> Open in new tab
            </button>
            <button
              onClick={onClose}
              aria-label="Close preview"
              className="rounded-md p-1.5 text-gray-300 hover:bg-gray-600 hover:text-white"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden bg-gray-900">
          {status === "loading" && (
            <div className="flex h-full items-center justify-center">
              <div className="size-8 animate-spin rounded-full border-2 border-gray-600 border-t-orange-500" />
            </div>
          )}
          {status === "error" && (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-red-400">
              Could not load this file. Try downloading it instead.
            </div>
          )}
          {status === "ready" && kind === "image" && (
            <div className="flex h-full items-center justify-center overflow-auto p-4">
              <img
                src={blobUrl}
                alt={attachment.filename}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          )}
          {status === "ready" && kind === "pdf" && (
            <iframe
              src={blobUrl}
              title={attachment.filename}
              className="h-full w-full"
            />
          )}
          {status === "ready" && kind === "other" && (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-300">
              No preview available for this file type. Use Download.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- attachment list ---------- */

function AttachmentList({ correspondenceId, attachments, onPreview }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {attachments.map((att, i) => {
        const kind = getKind(att);
        const { Icon, color, bg } = kindStyles[kind];
        return (
          <div
            key={getId(att) || i}
            className="group flex items-center gap-3 rounded-lg border border-gray-600 bg-gray-800/60 p-3 transition hover:border-orange-500/60"
          >
            <button
              onClick={() =>
                kind === "other"
                  ? downloadAttachment(correspondenceId, att)
                  : onPreview({ correspondenceId, attachment: att })
              }
              className="flex min-w-0 flex-1 items-center gap-3 text-left"
              title={kind === "other" ? "Download" : "Preview"}
            >
              <span
                className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${bg}`}
              >
                <Icon className={`size-5 ${color}`} />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-gray-100 group-hover:text-orange-300">
                  {att.filename}
                </span>
                <span className="block text-xs text-gray-400">
                  {kindLabel(att)}
                  {att.size != null && ` • ${formatSize(att.size)}`}
                </span>
              </span>
            </button>
            <div className="flex shrink-0 gap-1">
              <button
                onClick={() => openAttachmentInNewTab(correspondenceId, att)}
                aria-label={`Open ${att.filename} in new tab`}
                title="Open in new tab"
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-600 hover:text-orange-300"
              >
                <ArrowTopRightOnSquareIcon className="size-4" />
              </button>
              <button
                onClick={() => downloadAttachment(correspondenceId, att)}
                aria-label={`Download ${att.filename}`}
                title="Download"
                className="rounded-md p-1.5 text-gray-400 hover:bg-gray-600 hover:text-orange-300"
              >
                <ArrowDownTrayIcon className="size-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- main component ---------- */

const inputClass =
  "p-2 text-sm text-gray-200 bg-gray-600 border border-gray-500 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 placeholder-gray-400";

export default function CorrespondenceTab({ project }) {
  const [selectedCorrespondence, setSelectedCorrespondence] = useState(null);
  const [correspondences, setCorrespondences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("subject");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [onlyWithAttachments, setOnlyWithAttachments] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const fetchCorrespondences = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE}/correspondence/project/${project._id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setCorrespondences(response.data.data || []);
      } catch (err) {
        console.error("Error fetching correspondences:", err);
        setError("Could not load correspondence. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchCorrespondences();
  }, [project._id]);

  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchQuery("");
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setOnlyWithAttachments(false);
  };

  const filteredCorrespondences = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
    const end = endDate ? new Date(`${endDate}T23:59:59.999`) : null;

    return correspondences.filter((item) => {
      if (onlyWithAttachments && !(item.attachments?.length > 0)) return false;

      if (searchType === "date") {
        const itemDate = new Date(getDate(item));
        return (!start || itemDate >= start) && (!end || itemDate <= end);
      }

      if (!q) return true;
      const value =
        searchType === "sender"
          ? item.sender
          : searchType === "subject"
            ? item.subject
            : item.body;
      return (value || "").toLowerCase().includes(q);
    });
  }, [
    correspondences,
    searchQuery,
    searchType,
    startDate,
    endDate,
    onlyWithAttachments,
  ]);

  const hasActiveFilters =
    searchQuery || startDate || endDate || onlyWithAttachments;

  const selected = selectedCorrespondence;
  const recipients = selected?.recipients || [];
  const cc = selected?.cc || [];
  const attachments = selected?.attachments || [];

  return (
    <div className="bg-gray-900 p-1">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* ---------- Left: filters + timeline ---------- */}
        <div className="lg:w-2/5 xl:w-1/3 bg-gray-800 rounded-lg border border-gray-600 shadow-md flex flex-col">
          <div className="p-5 border-b border-gray-600 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Correspondence
              </h2>
              <span className="rounded-full bg-gray-600 px-2.5 py-0.5 text-xs text-gray-300">
                {filteredCorrespondences.length}
                {hasActiveFilters ? ` of ${correspondences.length}` : ""}
              </span>
            </div>

            <div className="flex gap-2">
              <select
                value={searchType}
                onChange={handleSearchTypeChange}
                className={`${inputClass} w-28 shrink-0`}
              >
                <option value="subject">Subject</option>
                <option value="sender">Sender</option>
                <option value="body">Body</option>
                <option value="date">Date</option>
              </select>

              {searchType === "date" ? (
                <div className="flex min-w-0 flex-1 gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`${inputClass} min-w-0 flex-1`}
                    aria-label="Start date"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`${inputClass} min-w-0 flex-1`}
                    aria-label="End date"
                  />
                </div>
              ) : (
                <div className="relative min-w-0 flex-1">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`Search by ${searchType}`}
                    className={`${inputClass} w-full pl-8`}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setOnlyWithAttachments((v) => !v)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${
                  onlyWithAttachments
                    ? "border-orange-500 bg-orange-500/20 text-orange-300"
                    : "border-gray-500 bg-gray-600 text-gray-300 hover:bg-gray-500"
                }`}
              >
                <PaperClipIcon className="size-3.5" />
                With attachments
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-orange-400 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          <div className="p-5 max-h-[calc(100vh-280px)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-700">
            {loading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="h-16 animate-pulse rounded-lg bg-gray-600"
                  />
                ))}
              </div>
            ) : error ? (
              <p className="text-center text-sm text-red-400">{error}</p>
            ) : (
              <CorrespondenceTimeline
                correspondences={filteredCorrespondences}
                onSelect={setSelectedCorrespondence}
                selectedId={getId(selected)}
              />
            )}
          </div>
        </div>

        {/* ---------- Right: details ---------- */}
        <div className="lg:flex-1 min-w-0 bg-gray-800 rounded-lg border border-gray-600 shadow-md">
          {selected ? (
            <div>
              <div className="border-b border-gray-600 p-6">
                <h2 className="text-xl font-semibold text-white tracking-tight break-words">
                  {selected.subject || "(No subject)"}
                </h2>
                <p className="mt-1 text-sm text-gray-400">
                  {new Date(getDate(selected)).toLocaleString()}
                </p>
              </div>

              <div className="space-y-6 p-6">
                <div className="grid gap-x-4 gap-y-2 rounded-lg bg-gray-800/50 p-4 text-sm sm:grid-cols-[auto_1fr]">
                  <span className="font-medium text-gray-400">From</span>
                  <span className="break-all text-gray-200">
                    {selected.sender}
                  </span>
                  <span className="font-medium text-gray-400">To</span>
                  <span className="break-all text-gray-200">
                    {recipients.join(", ") || "—"}
                  </span>
                  {cc.length > 0 && (
                    <>
                      <span className="font-medium text-gray-400">CC</span>
                      <span className="break-all text-gray-200">
                        {cc.join(", ")}
                      </span>
                    </>
                  )}
                </div>

                <div className="rounded-lg border border-gray-600 bg-gray-800/40 p-5">
                  <p className="whitespace-pre-wrap break-words text-gray-200 leading-relaxed">
                    {selected.body || "(No message body)"}
                  </p>
                </div>

                {attachments.length > 0 && (
                  <div>
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-300">
                      <PaperClipIcon className="size-4 text-orange-400" />
                      Attachments ({attachments.length})
                    </h3>
                    <AttachmentList
                      correspondenceId={getId(selected)}
                      attachments={attachments}
                      onPreview={setPreview}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 p-10 text-center">
              <EnvelopeOpenIcon className="size-12 text-gray-500" />
              <p className="text-gray-300 font-medium">
                No correspondence selected
              </p>
              <p className="text-sm text-gray-400">
                Pick an item from the timeline to read it and open its
                attachments.
              </p>
            </div>
          )}
        </div>
      </div>

      <AttachmentPreviewModal
        preview={preview}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}
